"use client"

import type { ResponseError } from "@/libs/axios"
import type { AxiosError } from "axios"
import type { FC, PropsWithChildren } from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { useRouter } from "next/navigation"
import { useCallback, useEffect } from "react"
import { useNotice } from "@/components/ui"
import { axiosEvent, axiosInstance } from "@/libs/axios"

export const client = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
})

export interface ApiProviderProps extends PropsWithChildren {
  cookie: string
  csrfToken: string
}

export const ApiProvider: FC<ApiProviderProps> = ({
  children,
  cookie,
  csrfToken,
}) => {
  const router = useRouter()
  const notice = useNotice()

  axiosInstance.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken
  axiosInstance.defaults.headers.common.Cookie = cookie

  const onError = useCallback(
    ({ message, response, status }: AxiosError<ResponseError>) => {
      if (status === 401) {
        router.push("/admins")
      }

      const title = response?.data.info?.message ?? message

      notice({ status: "error", title })
    },
    [notice, router],
  )

  useEffect(() => {
    axiosEvent.on("ServerError", onError)
    axiosEvent.on("ClientError", onError)

    return () => {
      axiosEvent.off("ServerError", onError)
      axiosEvent.off("ClientError", onError)
    }
  }, [onError])

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>
}
